import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { IonRouterOutlet } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { onMessage } from 'firebase/messaging';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route } from 'react-router';
import InAppWeb from '@/components/common/InAppWeb/InAppWeb';
import { ROUTES_PATH } from '@/constants';
import useMediaQuery, { Breakpoint } from '@/hooks/useMediaQuery';
import ChangePassword from '@/pages/Login/ChangePassword/ChangePassword';
import Login from '@/pages/Login/Login';
import ResetPassword from '@/pages/Login/ResetPassword/ResetPassword';
import OnBoarding from '@/pages/OnBoarding/OnBoarding';
import { ActualizaDatosSS } from '@/pages/ActualizaDatosSS/ActualizaDatosSS';
import { AltaClavePin } from '@/pages/AltaClavePin/AltaClavePin';
import { PrivacyPolicy } from '@/pages/PrivacyPolicy/PrivacyPolicy';
import TermsUse from '@/pages/TermsUse/TermsUse';
import { selectIsLogged, selectMustChangePassword } from '@/redux/auth/auth';
import { browserPermissions, updateNotificationStatus } from '@/redux/notifications/notifications';
import { Analytics } from '@/services/analytics';
import { initializeClarity } from '@/services/clarity';
import { notificationService } from '@/services/notifications';
import {
  checkWebInAppMessages,
  deregisterWebDevice,
  initializeWebNotifications,
  retrieveWebInAppMessages,
  WebNotificationPermission,
  WebNotificationStatus,
} from '@/services/notificationsWeb';
import { storageService } from '@/services/storage';
import { RootState } from '@/store';
import historyFile from '@/utils/history';

import DesktopLayout from './_layouts/Desktop';
import MobileLayout from './_layouts/Mobile';
import ForcedPasswordChangeRoutes from './ForcedPasswordChangeRoutes';
import { LoginSSO } from '@/pages/Login/LoginSSO/LoginSSO';

// Usuario que accede desde chop con el link de Comercios: ?user=XXXYYYY
const entryUser = new URLSearchParams(window.location.search).get('user');
if (entryUser) storageService.setEntryUserChop(entryUser);

export default function Router() {
  const dispatch = useDispatch();
  const isLogged = useSelector((state: RootState) => selectIsLogged(state));
  const mustChangePassword = useSelector((state: RootState) => selectMustChangePassword(state));
  const notificationSwitchChanged = useSelector((state: RootState) => state.notifications.switchChange);
  const notificationStatus = useSelector((state: RootState) => state.notifications.status);
  const initialized = useRef(false);
  const [inAppMessage, setInAppMessage] = useState<any>(null);
  const isFirstTime = storageService.getOnboardingStorage();
  const isDesktop = useMediaQuery(Breakpoint.Tablet);

  const documentVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      if (isLogged && localStorage.getItem('webNotificationStatus') === WebNotificationStatus.GRANTED) {
        checkWebInAppMessages()
          .then((currentMessage) => {
            if (currentMessage) {
              setInAppMessage(currentMessage);
            }
          });
      }
    }
  };

  const startNotificationsListener = () => {
    const messaging = initializeWebNotifications();
    dispatch(updateNotificationStatus(WebNotificationStatus.GRANTED));
    onMessage(messaging, () => {
      if (isLogged && notificationStatus === WebNotificationStatus.GRANTED) {
        retrieveWebInAppMessages()
          .then((response) => {
            const { nodes } = response?.data;
            const inAppMessages = nodes.find((n: any) => n.name === 'inAppMessages');
            const currentMessage = inAppMessages.items?.at(0);
            setInAppMessage(currentMessage);
          });
      }
    });
    document.addEventListener('visibilitychange', documentVisibilityChange);
  };

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      initializeClarity();
      Analytics.initialize();
    }
    const notifications = localStorage.getItem('webNotificationStatus');
    const fcmToken = storageService.getFcmToken();
    if (notifications) {
      if (notifications === WebNotificationStatus.GRANTED) {
        dispatch(updateNotificationStatus(WebNotificationStatus.GRANTED));
      } else if (notifications === WebNotificationStatus.DENIED) {
        dispatch(updateNotificationStatus(WebNotificationStatus.DENIED));
        if (fcmToken) {
          storageService.removeNotificationToken();
        }
      } else {
        dispatch(updateNotificationStatus(WebNotificationStatus.NOT_ESTABLISHED));
      }
    }
  },[]);  

  useEffect(() => {
    if (Capacitor.isNativePlatform()
      && Capacitor.getPlatform() === 'android'
      && !storageService.getInitialNotificationRequested()) {
      notificationService.canRequestPermissions(true).then((result) => {
        if (result) {
          LocalNotifications.requestPermissions().then(() => {
            storageService.initialNotificationRequested();
          });
        }
      });
    }
    if (isLogged) {
      if (Capacitor.isNativePlatform()) {
        notificationService.canRequestPermissions().then((result) => {
          if (result) {
            notificationService.enableNotifications();
          }
        });
      } else {
        LocalNotifications.checkPermissions().then((result) => {
          if (result.display === 'prompt') {
            storageService.removeNotificationToken();
            dispatch(browserPermissions(WebNotificationPermission.PROMPT));
            LocalNotifications.requestPermissions().then((permission) => {
              if (permission.display === 'granted') {
                dispatch(browserPermissions(WebNotificationPermission.GRANTED));
                dispatch(updateNotificationStatus(WebNotificationStatus.GRANTED));
                startNotificationsListener();
              } else if (permission.display === 'denied') {
                dispatch(browserPermissions(WebNotificationPermission.DENIED));
                dispatch(updateNotificationStatus(WebNotificationStatus.DENIED));
                if (storageService.getFcmToken()) {
                  deregisterWebDevice();
                }
              }
            });
          } else if (result.display === 'granted') {
            dispatch(browserPermissions(WebNotificationPermission.GRANTED));
            if (localStorage.getItem('webNotificationStatus') !== WebNotificationStatus.DENIED) {
              dispatch(updateNotificationStatus(WebNotificationStatus.GRANTED));
              startNotificationsListener();
            }
          } else if (result.display === 'denied') {
            dispatch(browserPermissions(WebNotificationPermission.DENIED));
            dispatch(updateNotificationStatus(WebNotificationStatus.DENIED));
            if (storageService.getFcmToken()) {
              deregisterWebDevice();
            }
          }
        });
      }
    }

    return () => {
      document.removeEventListener('visibilitychange', documentVisibilityChange);
    };
  }, [isLogged]);

  useEffect(() => {
    const notificationsInitialized = !!storageService.getFcmToken();
    const notifications = localStorage.getItem('webNotificationStatus');
    // Se activa cuando el usuario cambia el switch de notificaciones
    if (notificationSwitchChanged !== null) {
      if (notifications === WebNotificationStatus.GRANTED && !notificationsInitialized) {
        startNotificationsListener();
      } else {
        deregisterWebDevice();
      }
    }
  }, [notificationSwitchChanged]);

  function renderLayout() {
    if (mustChangePassword) {
      return <ForcedPasswordChangeRoutes />;
    }
    if (isDesktop) {
      return <DesktopLayout />;
    }
    return <MobileLayout />;
  }

  return (
    <IonReactRouter history={historyFile}>
      {inAppMessage && <InAppWeb message={inAppMessage} />}
      {isLogged ? renderLayout() : (
        <IonRouterOutlet id="main-router-outlet">
          <Route exact path="/">
            <Redirect to={ROUTES_PATH.LOGIN} />
          </Route>
          <Route
            exact
            path={ROUTES_PATH.LOGIN}
          >
            {isFirstTime === 'false' || !Capacitor.isNativePlatform() ? <Login /> : <Redirect to={ROUTES_PATH.ONBOARDING} />}
          </Route>
          <Route exact path={ROUTES_PATH.LOGIN_RESET_PASSWORD}>
            <ResetPassword />
          </Route>
          {/* Login rechazado con USR0032: cambia la clave antes de tener sesión */}
          <Route exact path={ROUTES_PATH.LOGIN_CHANGE_PASSWORD}>
            <ChangePassword expired />
          </Route>
          <Route exact path={ROUTES_PATH.ONBOARDING}>
            <OnBoarding />
          </Route>
          <Route exact path={ROUTES_PATH.TERMS_USE}>
            <TermsUse />
          </Route>
          <Route exact path={ROUTES_PATH.PRIVACY_POLICY}>
            <PrivacyPolicy />
          </Route>
          <Route exact path={ROUTES_PATH.ACTUALIZA_DATOS_SS}>
            <ActualizaDatosSS />
          </Route>
          <Route exact path={ROUTES_PATH.ALTA_CLAVE_PIN}>
            <AltaClavePin />
          </Route>
          <Route exact path={ROUTES_PATH.LOGIN_SSO}>
            <LoginSSO />
          </Route>
          <Route>
            <Redirect to={ROUTES_PATH.LOGIN} />
          </Route>
        </IonRouterOutlet>
      )}
    </IonReactRouter>
  );
}
